import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { History } from 'history';
import queryString, { ParsedQuery } from 'query-string';
import { PageLoadingIndicator } from '@spotify-internal/anchor-ds';

import AnchorAPI from 'modules/AnchorAPI';
import { getIsMinimallySetup } from 'modules/Podcast';
import { PODCAST_COLORS } from 'client/utils';
import { useCurrentUserCtx } from 'client/contexts/CurrentUser';
import { Footer } from 'client/components/Footer';
import { localStorage } from 'client/modules/Browser/localStorage';
import { useCreateEpisode } from 'hooks/episodes/useCreateEpisode';
import { Podcast } from 'client/types';
import {
  IntegrationRedirectStates,
  INTEGRATION_LANDING_PAGE,
  INTEGRATION_REDIRECT_STATES,
  IS_EXISTING_USER_KEY,
  VALID_VIDEO_PARTNERS,
} from './constants';
import {
  IntegrationRedirectScreenError,
  IntegrationRedirectScreenPodcastConfirmation,
} from './components';

const isPartnerValid = (
  integrationName: string,
  integrationData: ParsedQuery<string>
) => {
  return (
    Object.keys(VALID_VIDEO_PARTNERS).includes(integrationName) &&
    VALID_VIDEO_PARTNERS[integrationName].requiredParams.every(
      (key: string) => !!integrationData[key]
    )
  );
};

export function IntergrationRedirectScreen({ history }: { history: History }) {
  const [currentState, setCurrentState] = useState<IntegrationRedirectStates>(
    INTEGRATION_REDIRECT_STATES.IDLE
  );
  const [podcastData, setPodcastData] = useState<Podcast | null>(null);
  const {
    state: { name: userName, status, userId, webStationId },
    fetchCurrentUser,
  } = useCurrentUserCtx();
  const { createNewEpisode } = useCreateEpisode();
  const { search } = useLocation();
  const { integrationName } = useParams<{ integrationName: string }>();
  const integrationData = queryString.parse(decodeURIComponent(search));
  const isPartnerAndDataValid = isPartnerValid(
    integrationName,
    integrationData
  );

  // TODO https://anchorfm.atlassian.net/browse/CAST-2761: Connect BE Endpoint to update
  // user with the integration and update current state accordingly
  const handleDatabaseUpdate = useCallback(() => {
    // We are just setting the state to PODCAST for now until the TODO above is completed
    setCurrentState(INTEGRATION_REDIRECT_STATES.PODCAST);
  }, []);

  // If any step has failed, we redirect them to the integration
  // landing page, while passing the riverside data as query params
  const handleLoggedOutRedirect = useCallback(
    (forceCreateAccount?: boolean) => {
      setCurrentState(INTEGRATION_REDIRECT_STATES.COMPLETE);
      localStorage.getItem(IS_EXISTING_USER_KEY).then(isExistingUser => {
        // TODO https://anchorfm.atlassian.net/browse/CAST-2761: Add Integration Name when redirecting
        // to lander so we can redirect back here correctly and know what integration it is
        const queryParamObject = {
          integrationName,
          ...integrationData,
          ...((!isExistingUser || forceCreateAccount) && { create: true }),
        };
        const queryParamString = encodeURIComponent(
          queryString.stringify(queryParamObject)
        );
        history.replace(`${INTEGRATION_LANDING_PAGE}?${queryParamString}`);
      });
    },
    []
  );

  // Fetch a user's podcast and confirm if it is set up or not. Then set state accordingly
  const handlePodcastConfirmation = useCallback(async () => {
    const podcast = await AnchorAPI.fetchCurrentUsersPodcast();
    const isMinimallySetup = getIsMinimallySetup(podcast);

    if (isMinimallySetup) {
      if (!Object.keys(podcastData || {}).length) {
        setPodcastData(podcast);
      }
      setCurrentState(INTEGRATION_REDIRECT_STATES.CONFIRMATION);
    } else {
      setCurrentState(INTEGRATION_REDIRECT_STATES.SUCCESS);
    }
  }, []);

  // When all steps have been successful, we create a new shell episode and redirect
  // to Episode Options page for new episode
  const handleSuccess = useCallback(() => {
    setCurrentState(INTEGRATION_REDIRECT_STATES.COMPLETE);
    createNewEpisode(
      {},
      {
        onSuccess: data => {
          const queryParamString = encodeURIComponent(
            queryString.stringify({
              ...integrationData,
              integrationName,
            })
          );
          history.push(
            `/dashboard/episode/${data.webEpisodeId}/metadata/edit?${queryParamString}`
          );
        },
      }
    );
  }, []);

  // The flow and states for the Integration Redirect Screen are as follows:
  //  * Current User Context Flow
  //    * If the user context returns a status of "success", a userId, and a
  //      webStationId, we begin the the Integration Redirect Flow (see below).
  //    * If the user context returns a status of "error" or a status of "success"
  //      with no userId or webStationId, we log the user out and send them to the
  //      Integration Connection Screen to login or sign up.
  useEffect(() => {
    if (
      isPartnerAndDataValid &&
      currentState === INTEGRATION_REDIRECT_STATES.IDLE
    ) {
      if (status === 'idle') {
        fetchCurrentUser();
      } else if (status === 'error') {
        handleLoggedOutRedirect();
      } else if (status === 'success') {
        if (userId && webStationId) {
          handleDatabaseUpdate();
        } else {
          handleLoggedOutRedirect();
        }
      }
    }
  }, [
    currentState,
    fetchCurrentUser,
    handleDatabaseUpdate,
    handleLoggedOutRedirect,
    isPartnerAndDataValid,
    status,
    userId,
    webStationId,
  ]);

  // Integration Redirect Flow:
  //  * IDLE state: FE tells the BE to update the user with the specific integration type.
  //    The BE will confirm if this is the user's first time and FE takes one of the following actions:
  //    * First time: State changes to FAIL.
  //      NOTE: Upon successful login or sign up, the user is returned to this screen
  //      and the BE will say this is not the user's first time. They will not hit FAIL again.
  //    * Not First time: State changes to PODCAST
  //
  //  * PODCAST state: FE fetches user's podcast, confirms its status, and chooses next action:
  //    * Set up: State changes to CONFIRMATION
  //    * Not set up: State changes to SUCCESS
  //      NOTE: Even though FE says success and sends the user through, the current uploader flow
  //      will require the user to set up their podcast in full before publishing which is why
  //      the user does not provide confirmation in the Integration Redirect flow.
  //
  //  * CONFIRMATION State: FE displays the Integration Redirect Screen Podcast Confirmation components.
  //    There are two actions that can be taken by the user:
  //      * Confirm podcast: State changes to SUCCESS
  //      * Reject podcast: State changes to FAIL
  //
  //  * SUCCESS State: FE creates a new episode using the createNewEpisode hook and redirects the user
  //    to the episode uploader flow. FE also updates the current state to COMPLETE
  //
  //  * FAIL state: The user is logged out and redirected to the Integration Connection Screen. FE also
  //    updates the current state to COMPLETE
  //
  //  * COMPLETE state: A flag that the flow is completed
  if (currentState === INTEGRATION_REDIRECT_STATES.PODCAST) {
    handlePodcastConfirmation();
  } else if (currentState === INTEGRATION_REDIRECT_STATES.SUCCESS) {
    handleSuccess();
  } else if (currentState === INTEGRATION_REDIRECT_STATES.FAIL) {
    handleLoggedOutRedirect();
  }

  return (
    <>
      <div className="pageContainer">
        {!isPartnerAndDataValid ? (
          <IntegrationRedirectScreenError integrationName={integrationName} />
        ) : currentState === INTEGRATION_REDIRECT_STATES.CONFIRMATION &&
          podcastData ? (
          <IntegrationRedirectScreenPodcastConfirmation
            handleSuccess={handleSuccess}
            handleLoggedOutRedirect={handleLoggedOutRedirect}
            podcastData={podcastData}
            userName={userName}
          />
        ) : (
          <PageLoadingIndicator color={PODCAST_COLORS.WHITE.color} />
        )}
      </div>
      <Footer />
    </>
  );
}
